import { ReactNode } from 'react';

export interface Column<T = any> {
  id: string;
  label: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  selectedRowId?: string | number;
  getId?: (row: T) => string | number;
}

/**
 * DataTable component - Reusable table component for displaying tabular data
 * 
 * Features:
 * - Custom cell rendering support
 * - Row click handler
 * - Empty state message
 * - Responsive: horizontal scroll on mobile/tablet, full table on desktop
 * - Accessible: semantic table structure
 */
export const DataTable = <T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'No data available',
  selectedRowId,
  getId = (row: T) => row.id,
}: DataTableProps<T>) => {
  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-text-secondary">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-background-secondary border-b border-border">
            {columns.map((column) => (
              <th
                key={column.id}
                className={`
                  px-6 py-3 text-left text-sm font-medium text-text-primary
                  ${column.className || ''}
                `}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => {
            const rowId = getId(row);
            const isSelected = selectedRowId !== undefined && rowId === selectedRowId;
            return (
              <tr
                key={rowIndex}
                onClick={() => onRowClick?.(row)}
                className={`
                  border-b border-border transition-colors
                  ${onRowClick ? 'cursor-pointer hover:bg-background-secondary' : ''}
                  ${isSelected ? 'bg-background-secondary' : ''}
                `}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={`
                      px-6 py-4 text-base text-text-primary
                      ${column.className || ''}
                    `}
                  >
                    {column.render ? column.render(row) : row[column.id]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

